/** Non-technical view of an assistant proposal.
 *
 * Renders the `user_summary` produced by the backend's second-pass LLM call:
 * headline, paragraph, bullet list, what-happens-if-accepted, and any
 * warnings. No JSON, no schema paths, no tool names. The advanced view
 * (`ProposedChangesCard`) is the escape hatch when the operator needs detail.
 */
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { AlertTriangle, CheckCircle2, Info } from 'lucide-react'
import type { AssistantChatResult } from '../api/assistant-api'

interface Props {
  result: AssistantChatResult
  accepted: boolean | null
  onAccept: () => void
  onReject: () => void
}

export function SimpleChangesCard({ result, accepted, onAccept, onReject }: Props) {
  const summary = result.user_summary
  const ops = result.patch || []
  const isNoop = ops.length === 0

  // Server didn't return a summary (older backend, summary disabled, or error
  // before the second pass). Render a minimal fallback that still lets the
  // user accept/reject without leaving simple mode.
  if (!summary) {
    return (
      <FallbackCard
        result={result}
        accepted={accepted}
        onAccept={onAccept}
        onReject={onReject}
      />
    )
  }

  return (
    <div className="mt-2 rounded-md border border-border bg-card shadow-sm" dir="rtl">
      <div className="flex flex-row items-center gap-2 p-3 border-b border-border">
        <span className="text-sm font-semibold">{summary.headline || 'تغییرات پیشنهادی'}</span>
        {result.form_valid ? (
          <Badge variant="outline" className="text-emerald-400 border-emerald-500/40 gap-1 text-[10px]">
            <CheckCircle2 className="w-3 h-3" /> معتبر
          </Badge>
        ) : (
          <Badge variant="outline" className="text-destructive border-destructive/40 gap-1 text-[10px]">
            <AlertTriangle className="w-3 h-3" /> نامعتبر
          </Badge>
        )}
        <Badge variant="secondary" className="text-[10px]">{ops.length} تغییر</Badge>
      </div>

      <div className="p-3 space-y-3">
        {summary.paragraph && (
          <p className="text-sm leading-relaxed text-foreground">{summary.paragraph}</p>
        )}

        {summary.bullets.length > 0 && (
          <ul className="text-sm space-y-1 list-disc list-inside text-foreground">
            {summary.bullets.map((b, i) => (
              <li key={i}>{b}</li>
            ))}
          </ul>
        )}

        {summary.if_accepted && (
          <div className="rounded-md border border-primary/30 bg-primary/5 p-2.5 text-xs flex gap-2">
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-primary" />
            <div>
              <div className="font-medium text-foreground">اگر تأیید کنید:</div>
              <div className="text-muted-foreground mt-0.5">{summary.if_accepted}</div>
            </div>
          </div>
        )}

        {summary.warnings.length > 0 && (
          <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-2.5 text-xs space-y-1">
            <div className="flex items-center gap-1.5 font-medium text-amber-300">
              <AlertTriangle className="w-3.5 h-3.5" /> توجه
            </div>
            <ul className="list-disc list-inside text-foreground">
              {summary.warnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="flex items-center justify-end gap-2 p-3 border-t border-border">
        {accepted === null ? (
          <>
            <Button variant="outline" size="sm" onClick={onReject}>رد</Button>
            <Button size="sm" onClick={onAccept} disabled={isNoop || !result.form_valid}>
              تأیید و اعمال
            </Button>
          </>
        ) : accepted ? (
          <Badge variant="outline" className="text-emerald-400 border-emerald-500/40">اعمال شد</Badge>
        ) : (
          <Badge variant="secondary">رد شد</Badge>
        )}
      </div>
    </div>
  )
}

function FallbackCard({ result, accepted, onAccept, onReject }: Props) {
  const ops = result.patch || []
  // Intentionally NO bullets here — tool_trace summaries are English /
  // technical ("upserted field 'height_cm'"). Simple mode must never leak
  // those. Plain-language bullets come ONLY from the backend's user_summary.
  return (
    <div className="mt-2 rounded-md border border-border bg-card shadow-sm" dir="rtl">
      <div className="flex flex-row items-center gap-2 p-3 border-b border-border">
        <span className="text-sm font-semibold">{ops.length} تغییر پیشنهاد شد</span>
        {result.form_valid ? (
          <Badge variant="outline" className="text-emerald-400 border-emerald-500/40 gap-1 text-[10px]">
            <CheckCircle2 className="w-3 h-3" /> معتبر
          </Badge>
        ) : (
          <Badge variant="outline" className="text-destructive border-destructive/40 gap-1 text-[10px]">
            <AlertTriangle className="w-3 h-3" /> نامعتبر
          </Badge>
        )}
      </div>
      <div className="p-3 space-y-3">
        <p className="text-sm text-foreground">
          دستیار تغییراتی برای فرم پیشنهاد داده است.
        </p>
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-2.5 text-xs space-y-1">
          <div className="flex items-center gap-1.5 font-medium text-amber-300">
            <AlertTriangle className="w-3.5 h-3.5" /> خلاصهٔ متنی در دسترس نیست
          </div>
          <p className="text-muted-foreground">
            سرویس دستیار نتوانست خلاصهٔ ساده تولید کند. برای بررسی دقیق تغییرات قبل از تأیید،
            به حالت <span className="font-semibold text-foreground">پیشرفته</span> بروید.
          </p>
        </div>
      </div>
      <div className="flex items-center justify-end gap-2 p-3 border-t border-border">
        {accepted === null ? (
          <>
            <Button variant="outline" size="sm" onClick={onReject}>رد</Button>
            <Button size="sm" onClick={onAccept} disabled={ops.length === 0 || !result.form_valid}>
              تأیید و اعمال
            </Button>
          </>
        ) : accepted ? (
          <Badge variant="outline" className="text-emerald-400 border-emerald-500/40">اعمال شد</Badge>
        ) : (
          <Badge variant="secondary">رد شد</Badge>
        )}
      </div>
    </div>
  )
}
